import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DateTime } from 'luxon'
import { stringify } from 'query-string'
import { useTranslate } from 'ra-core'
import { useGetList } from 'react-admin'
import { Link as RouterLink } from 'react-router-dom'

import { getUserFullName } from '../../components/UserFullNameField'
import { BOOKING_ERA_UPCOMING } from '../../config/bookings'
import { USER_ACCOUNTS_CLOSING_REASON_SUSPICIOUS_ACTIVITY, USER_ACCOUNTS_CLOSING_REASONS } from '../../config/users'
import { useShowStyles } from '../common/show'

const DeactivateAccountConfirmContent = ({ record, update, state, isPristine, error }) => {
  const showClasses = useShowStyles()
  const translate = useTranslate()
  return (
    <>
      <Typography>
        {translate('resources.users.actions.deactivateAccount.helperText', {
          userFullName: getUserFullName(record),
        })}
        <br />
        {translate('resources.users.actions.deactivateAccount.helperText2')}
      </Typography>
      <FormControl style={{ marginTop: '1.5em', display: 'block' }}>
        <FormLabel component="legend" focused={false} style={{ marginBottom: '0.5em' }}>
          {translate('resources.users.fields.user_account.closing_reason')}
        </FormLabel>
        <RadioGroup name="closing_reason" required onChange={(event) => update({ closing_reason: event.target.value })}>
          {Object.entries(USER_ACCOUNTS_CLOSING_REASONS).map(([k, v]) => (
            <FormControlLabel key={k} value={k} control={<Radio />} label={translate(v)} />
          ))}
        </RadioGroup>
        <FormHelperText error>{!isPristine && translate(error?.closing_reason)}</FormHelperText>
      </FormControl>
      {state.closing_reason === USER_ACCOUNTS_CLOSING_REASON_SUSPICIOUS_ACTIVITY && (
        <FormControl style={{ marginTop: '1.5em', display: 'block' }}>
          <TextField
            name="closing_justification"
            label={translate('resources.users.fields.user_account.closing_justification')}
            onChange={(event) => update({ closing_justification: event.target.value })}
            sx={showClasses.textFieldWithFullWidth}
            size="small"
            multiline
            required
          />
          <FormHelperText error>{!isPristine && translate(error?.closing_justification)}</FormHelperText>
        </FormControl>
      )}
    </>
  )
}
const UpcomingBookingsWarning = ({ record }) => {
  const translate = useTranslate()
  const filter = { user_id: record.id, era: BOOKING_ERA_UPCOMING, cancelled: false }
  const { total } = useGetList('bookings', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'start_scheduled_on', order: 'ASC' },
    filter,
  })
  if (!total) {
    return null
  }
  return (
    <Alert severity="warning" style={{ marginTop: '1.5em' }}>
      {translate('resources.users.actions.suspendAccount.upcomingBookingsWarning', { count: total })}{' '}
      <Link
        component={RouterLink}
        to={{ pathname: '/bookings', search: stringify({ filter: JSON.stringify(filter) }) }}
      >
        {translate('resources.users.actions.suspendAccount.upcomingBookingsLink')}
      </Link>
    </Alert>
  )
}

export const SUSPENSION_KIND_UNTIL_DATE = 'until_date'
export const SUSPENSION_KIND_INDEFINITELY = 'indefinitely'
export const SUSPENSION_REASON_MAX_LENGTH = 2000
const SUSPENSION_REASON_WARNING_LENGTH = 1900

const SuspendAccountConfirmContent = ({
  record,
  update,
  state,
  isPristine,
  error,
  showUpcomingBookingsWarning,
  requiresReason,
}) => {
  const showClasses = useShowStyles()
  const translate = useTranslate()
  return (
    <>
      <Typography>
        {translate('resources.users.actions.suspendAccount.helperText', { userFullName: getUserFullName(record) })}
      </Typography>
      {showUpcomingBookingsWarning && <UpcomingBookingsWarning record={record} />}
      <FormControl style={{ marginTop: '1.5em', display: 'block' }}>
        <FormLabel component="legend" focused={false} style={{ marginBottom: '0.5em' }}>
          {translate('resources.users.actions.suspendAccount.kind')}
        </FormLabel>
        <RadioGroup
          name="suspension_kind"
          value={state.suspension_kind ?? null}
          onChange={(event) => update({ suspension_kind: event.target.value })}
        >
          <FormControlLabel
            value={SUSPENSION_KIND_UNTIL_DATE}
            control={<Radio />}
            label={translate('resources.users.actions.suspendAccount.untilDate')}
          />
          <FormControlLabel
            value={SUSPENSION_KIND_INDEFINITELY}
            control={<Radio />}
            label={translate('resources.users.actions.suspendAccount.indefinitely')}
          />
        </RadioGroup>
        <FormHelperText error>{!isPristine && translate(error?.suspension_kind)}</FormHelperText>
      </FormControl>
      {state.suspension_kind === SUSPENSION_KIND_UNTIL_DATE && (
        <FormControl style={{ marginTop: '1.5em', display: 'block' }}>
          <TextField
            name="suspended_until"
            type="date"
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: DateTime.now().toISODate() } }}
            label={translate('resources.users.actions.suspendAccount.suspendedUntil')}
            value={state.suspended_until ?? ''}
            onChange={(event) => update({ suspended_until: event.target.value || null })}
            sx={showClasses.textFieldWithFullWidth}
            size="small"
            required
          />
          <FormHelperText>{translate('resources.users.actions.suspendAccount.suspendedUntilHelper')}</FormHelperText>
          <FormHelperText error>{!isPristine && translate(error?.suspended_until)}</FormHelperText>
        </FormControl>
      )}
      {requiresReason && (
        <FormControl style={{ marginTop: '1.5em', display: 'block' }}>
          <TextField
            name="reason"
            label={translate('resources.users.actions.suspendAccount.reason')}
            value={state.reason ?? ''}
            onChange={(event) => update({ reason: event.target.value })}
            slotProps={{ htmlInput: { maxLength: SUSPENSION_REASON_MAX_LENGTH } }}
            sx={showClasses.textFieldWithFullWidth}
            size="small"
            multiline
            required
          />
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <FormHelperText>{translate('resources.users.actions.suspendAccount.reasonHelper')}</FormHelperText>
            <FormHelperText
              sx={{
                whiteSpace: 'nowrap',
                color: (state.reason ?? '').length >= SUSPENSION_REASON_WARNING_LENGTH ? 'warning.main' : undefined,
              }}
            >
              {translate('resources.users.actions.suspendAccount.reasonCounter', {
                count: (state.reason ?? '').length,
                max: SUSPENSION_REASON_MAX_LENGTH,
              })}
            </FormHelperText>
          </Stack>
          <FormHelperText error>{!isPristine && translate(error?.reason)}</FormHelperText>
        </FormControl>
      )}
    </>
  )
}

const validateSuspension = (state) => {
  if (!state.suspension_kind) {
    return { suspension_kind: 'resources.users.actions.suspendAccount.validate.kind' }
  }
  if (state.suspension_kind === SUSPENSION_KIND_UNTIL_DATE && !state.suspended_until) {
    return { suspended_until: 'resources.users.actions.suspendAccount.validate.suspendedUntil' }
  }
  if (state.suspension_kind === SUSPENSION_KIND_UNTIL_DATE && state.suspended_until < DateTime.now().toISODate()) {
    return { suspended_until: 'resources.users.actions.suspendAccount.validate.pastDate' }
  }
  return null
}

const toSuspensionPayload = (state) => ({
  suspended_until: state.suspension_kind === SUSPENSION_KIND_UNTIL_DATE ? state.suspended_until : null,
})

const validateSuspendAccount = (state) => {
  const suspensionError = validateSuspension(state)
  if (suspensionError) {
    return suspensionError
  }
  if (!state.reason?.trim()) {
    return { reason: 'resources.users.actions.suspendAccount.validate.reason' }
  }
  return null
}

export const suspendAccountConfirm = ({ record }) => ({
  validate: validateSuspendAccount,
  content: (props) => (
    <SuspendAccountConfirmContent record={record} showUpcomingBookingsWarning requiresReason {...props} />
  ),
  confirmLabel: 'resources.users.actions.suspendAccount.confirmLabel',
  toPayload: (state) => ({ ...toSuspensionPayload(state), reason: state.reason }),
})

const toSuspensionState = (suspension) =>
  suspension?.suspended_until
    ? { suspension_kind: SUSPENSION_KIND_UNTIL_DATE, suspended_until: suspension.suspended_until }
    : { suspension_kind: SUSPENSION_KIND_INDEFINITELY }

export const updateSuspensionConfirm = ({ record }) => ({
  validate: validateSuspension,
  content: (props) => <SuspendAccountConfirmContent record={record} {...props} />,
  confirmLabel: 'resources.users.actions.updateSuspension.confirmLabel',
  confirmColor: 'primary',
  toPayload: toSuspensionPayload,
  initialState: toSuspensionState(record.user_account?.suspension),
})

const UnsuspendAccountConfirmContent = ({ record }) => {
  const translate = useTranslate()
  return (
    <Typography>
      {translate('resources.users.actions.unsuspendAccount.helperText', { userFullName: getUserFullName(record) })}
    </Typography>
  )
}

export const unsuspendAccountConfirm = ({ record }) => ({
  content: () => <UnsuspendAccountConfirmContent record={record} />,
  confirmLabel: 'resources.users.actions.unsuspendAccount.confirmLabel',
  confirmColor: 'primary',
})

export const deactivateAccountConfirm = ({ record }) => ({
  validate: (state) => {
    if (!state.closing_reason) {
      return { closing_reason: 'resources.users.actions.deactivateAccount.validate.closingReason' }
    }
    if (state.closing_reason === USER_ACCOUNTS_CLOSING_REASON_SUSPICIOUS_ACTIVITY && !state.closing_justification) {
      return { closing_justification: 'resources.users.actions.deactivateAccount.validate.closingJustification' }
    }
    return null
  },
  content: (props) => <DeactivateAccountConfirmContent record={record} {...props} />,
  confirmLabel: 'resources.users.actions.deactivateAccount.confirmLabel',
})

const ReactivateAccountConfirmContent = ({ wasClosedRecently, recentDelay, record }) => {
  const translate = useTranslate()
  return (
    <>
      {wasClosedRecently ? (
        <Typography>
          {translate('resources.users.actions.reactivateAccount.helperText', {
            userFullName: getUserFullName(record),
          })}
        </Typography>
      ) : (
        <Typography>
          {translate('resources.users.actions.reactivateAccount.helperText2', { recentDelay: recentDelay })}
        </Typography>
      )}
    </>
  )
}

export const reactivateAccountConfirm = ({ record }) => {
  const recentDelay = 48 // hours
  const wasClosedRecently =
    record.user_account.closed_on &&
    new Date().getTime() < new Date(record.user_account.closed_on).getTime() + recentDelay * 3600 * 1000

  return {
    content: (props) => (
      <ReactivateAccountConfirmContent
        {...props}
        recentDelay={recentDelay}
        wasClosedRecently={wasClosedRecently}
        record={record}
      />
    ),
    confirmLabel: wasClosedRecently
      ? 'resources.users.actions.reactivateAccount.confirmLabel1'
      : 'resources.users.actions.reactivateAccount.confirmLabel2',
  }
}
